import _ from 'lodash';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { apiAxios } from '@/apiAxios';
import Batch from '@/classes/Batch';
import { IApiResponseBatchesList, IApiResponseBatchesSearch, IApiResponseBatchesUpdate } from '@/interfaces';
import { upsert } from '@/main';
import store from '@/store/store';

@Module({ dynamic: true, store, name: 'BatchesModule', namespaced: true })
export default class BatchesModule extends VuexModule {
  public batches = [] as Batch[];
  public currentPage = 0;
  public hasMore = true;

  @Mutation
  public setHasMore(newHasMore: boolean) {
    this.hasMore = newHasMore;
  }

  @Mutation
  public increasePageIndex() {
    this.currentPage++;
  }

  @Mutation
  public upsertBatches(newBatches: Batch[]) {
    const results = this.batches;
    _.each(newBatches, (newBatch) => {
      upsert<Batch>(results, { batchid: newBatch.batchid }, newBatch);
    });
    this.batches = results;
  }

  @Mutation
  public upsertBatch(newBatch: Batch) {
    const results = this.batches;
    upsert<Batch>(results, { batchid: newBatch.batchid }, newBatch);

    this.batches = results;
  }

  @Mutation
  public prependBatch(newBatch: Batch) {
    const results = this.batches;
    results.unshift(newBatch);

    this.batches = results;
  }

  @Action
  public async apiLoadNextPage(arg: { pageSize: number }): Promise<IApiResponseBatchesList> {
    try {
      const { pageSize } = arg;

      const response = await apiAxios.get<IApiResponseBatchesList>(`api/batch/page/${ this.currentPage }`, {
        params: { pagesize: pageSize },
      });
      const batches = response.data.payload.batches;

      if (batches.length > 0) {
        this.upsertBatches(response.data.payload.batches);
        this.increasePageIndex();

        if (batches.length < pageSize) {
          this.setHasMore(false);
        }
      } else {
        // No item
        this.setHasMore(false);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiAddBatch(arg: { batch: Batch }): Promise <IApiResponseBatchesUpdate> {
    try {
      const { batch } = arg;

      const response = await apiAxios.post<IApiResponseBatchesUpdate>('api/batch/create', batch);

      if (response.status === 200) {
        this.prependBatch(response.data.payload.batch);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiUpdateBatch(arg: { batch: Batch }): Promise <IApiResponseBatchesUpdate> {
    try {
      const { batch } = arg;

      const response = await apiAxios.post<IApiResponseBatchesUpdate>('api/batch/update', batch);

      this.upsertBatch(response.data.payload.batch);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiSearchBatches(arg: { searchText: string }): Promise<IApiResponseBatchesSearch> {
    try {
      const { searchText } = arg;

      const response = await apiAxios.post<IApiResponseBatchesSearch>('api/batch/search', { searchText });

      this.upsertBatches(response.data.payload.batches);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
